/**
 * A held link kept whole (D5 of docs/plans/qa-fixes-dairy.md): when the
 * records a link joins gain fields after it was drawn, the link carries them
 * too. Sharma Dairy, 18 Oct: Rate was added to Milk Movement (v39, v40) and
 * both links into it kept posting no rate, though both sources hold one; and
 * Milk Collection gained its own Entry Type (In, Out) after its link was drawn
 * (v25), and the link still marked every entry In.
 *
 * `heldLinkFills` reads, for each link into or out of the given records:
 *
 *   fills  each field of the target no fill writes, where the source holds a
 *          field of the same key or name and type (the same list, for a pick;
 *          no choice the target does not offer, for a choice), and the target
 *          is wrong without it (`targetNeeds`): its measure, a field its
 *          measure is worked out from, or one a worked-out value reads that
 *          counts a missing value as 0 (Rate under Total, when the link writes
 *          Quantity). A field the target is right without (a Customer, a Note,
 *          a Rate whose Total reads blank) is carried only where the turn
 *          asked for that field of the target by name (`options.fields`), and
 *          a choice never on its own: a repayment's Payment Mode is not the
 *          sale's (review of the fix diff, 18 Oct: a setup that read Ready
 *          still reads Ready). Never into a worked-out value, a lines field or
 *          the target's in/out field, and never a fill the person took off
 *          (`removed`).
 *   carry  the target goes in or out by a choice (`signFieldKey`), the link
 *          writes one fixed option of it, and the source has its own copy of
 *          that choice (`inOutChoiceOf`): the link carries the source's choice
 *          instead, so an Out entry posts Out. Only where every option's key
 *          is one the target offers, since a carried choice posts its key;
 *          where the keys differ, nothing is carried and the `same_job` check
 *          says so (`setup-gaps.ts`).
 *
 * `heldLinkFillOps` is the same as ops: one `update_link` with the whole new
 * fills per link that gains any. Pure, no model: the compile applies it for
 * the fields a turn asked for, and the `link_fill_missing` check offers the
 * rest as its chip, never changed on its own.
 */
import type { SetupDraftField } from '../compose-author';
import type { StudioDraft, StudioLink, StudioLinkFill, StudioRecord } from './draft';
import type { StudioOp } from './ops';

/** A field by its record and key: one a turn asked for, or one whose fill the person took off. */
export type HeldLinkField = { readonly record: string; readonly field: string };

export type HeldLinkFillOptions = {
  /**
   * The fields the person asked for in the turn. A fill writing one of these
   * fields of the target is carried; a fill reading one of the source's is
   * carried only where the target needs it (`targetNeeds`). Absent: every
   * fill the target needs.
   */
  readonly fields?: readonly HeldLinkField[];
  /** Fills the person took off, by link key and the target field (`to`): never added back. */
  readonly removed?: ReadonlyArray<{ readonly link: string; readonly to: string }>;
};

/** One field of the source standing for the target's in/out choice. */
export type HeldLinkCarry = {
  /** The target's in/out field (`signFieldKey`). */
  readonly to: string;
  /** The source's choice carried into it. */
  readonly field: string;
  /** The fixed option it replaces. */
  readonly replaces: string | number | boolean;
};

export type HeldLinkFills = {
  readonly link: string;
  /** Fills the link gains, in the target's field order. */
  readonly fills: readonly StudioLinkFill[];
  /** The source's own in/out choice carried in place of a fixed option, when it fits. */
  readonly carry: HeldLinkCarry | null;
};

const fold = (words: string): string => words.toLowerCase().replace(/\s+/g, ' ').trim();

/** Whether every choice the source can hold is one the target offers: a carried choice is posted as its key. */
const choicesFit = (source: SetupDraftField, target: SetupDraftField): boolean => {
  const offered = new Set((target.options ?? []).map((option) => option.key));
  return (source.options ?? []).every((option) => offered.has(option.key));
};

/**
 * Whether a field of the source can fill the target's: the same type (and
 * list, for a pick; and choices, for a choice). The name is matched by the
 * caller, key first, so a Rate whose key is `rate` carries into a Rate per
 * Liter of key `rate`.
 */
const fitsType = (source: SetupDraftField, target: SetupDraftField): boolean =>
  source.type === target.type &&
  ((target.type !== 'reference' && target.type !== 'multi_select') || source.targetRecordKey === target.targetRecordKey) &&
  (target.type !== 'select' || choicesFit(source, target));

/** A field a link can fill: never a worked-out value (it works itself out) and never a lines field (no link fills lines). */
const fillable = (field: SetupDraftField): boolean => field.type !== 'formula' && field.type !== 'lines';

/** Whether a worked-out value reads `fieldKey`, directly. */
const reads = (node: unknown, fieldKey: string): boolean => {
  if (Array.isArray(node)) return node.some((one) => reads(one, fieldKey));
  if (typeof node !== 'object' || node === null) return false;
  const held = node as Record<string, unknown>;
  if ((held.kind === 'field' && held.fieldKey === fieldKey) || held.of === fieldKey) return true;
  return Object.values(held).some((value) => reads(value, fieldKey));
};

/**
 * Whether `to` is wrong when a link leaves `fieldKey` empty: the field is its
 * measure, or a worked-out value reads it (through other worked-out values
 * too) that is its measure or counts a missing value as 0, so it shows a
 * figure that is not so. A choice never: which way a choice goes on one
 * entry is never another's to say. A worked-out value that reads blank
 * without it says it is not known, which is right.
 */
export const targetNeeds = (to: StudioRecord, fieldKey: string): boolean => {
  const field = to.fields.find((one) => one.key === fieldKey);
  if (field === undefined || field.type === 'select' || field.type === 'multi_select') return false;
  if (fieldKey === to.measureFieldKey) return true;
  const formulas = to.fields.filter((one) => one.type === 'formula' && one.formula !== undefined);
  const guarded = formulas.filter((one) => one.key === to.measureFieldKey || one.formula!.onMissing === 'zero');
  // The worked-out values a guarded one reads, and theirs, are guarded too.
  for (let at = 0; at < guarded.length; at += 1) {
    for (const one of formulas) if (!guarded.includes(one) && reads(guarded[at]!.formula, one.key)) guarded.push(one);
  }
  return guarded.some((one) => reads(one.formula, fieldKey));
};

/** The source field that carries the target's, by the same key, else the same name. */
const carrierOf = (from: StudioRecord, target: SetupDraftField): SetupDraftField | undefined =>
  from.fields.find((one) => one.key === target.key && fillable(one) && fitsType(one, target)) ??
  from.fields.find((one) => fold(one.label) === fold(target.label) && fillable(one) && fitsType(one, target));

/** Whether the link's conditions read `field` of the entry: a link keyed off its own choice is left as it is. */
const conditionsRead = (link: StudioLink, field: string): boolean => link.when.some((group) => group.any.some((one) => one.via === undefined && one.field === field));

/**
 * The source's own copy of the target's in/out choice: a choice of the
 * source, two options or more, each one an option of the target's in/out
 * field by its key or its name (with the same sign where the source gives
 * one), together going both ways. `byKey` when every option matched by its
 * key, so the choice can be carried as it is. Null when the target goes by no
 * choice or the source has no such copy.
 */
export const inOutChoiceOf = (from: StudioRecord, to: StudioRecord): { field: SetupDraftField; byKey: boolean } | null => {
  const sign = to.fields.find((one) => one.key === to.signFieldKey);
  if (sign === undefined || sign.type !== 'select') return null;
  const targetOptions = sign.options ?? [];
  for (const field of from.fields) {
    const options = field.type === 'select' ? (field.options ?? []) : [];
    if (options.length < 2) continue;
    let byKey = true;
    const signs = new Set<number>();
    const matched = options.every((option) => {
      const keyed = targetOptions.find((one) => one.key === option.key);
      const named = keyed ?? targetOptions.find((one) => fold(one.label) === fold(option.label));
      if (named === undefined || (named.sign !== 1 && named.sign !== -1)) return false;
      if (option.sign !== undefined && option.sign !== named.sign) return false;
      if (keyed === undefined) byKey = false;
      signs.add(named.sign);
      return true;
    });
    if (matched && signs.has(1) && signs.has(-1)) return { field, byKey };
  }
  return null;
};

/** What a link joining these records leaves unfilled that its source holds. */
const missingOf = (draft: StudioDraft, link: StudioLink, options: HeldLinkFillOptions): HeldLinkFills | null => {
  const from = draft.records.find((one) => one.key === link.from);
  const to = draft.records.find((one) => one.key === link.to);
  if (from === undefined || to === undefined) return null;
  const asked = options.fields === undefined ? null : new Set(options.fields.map((one) => `${one.record}.${one.field}`));
  const isAsked = (record: StudioRecord, field: string): boolean => asked === null || asked.has(`${record.key}.${field}`);
  /** Asked for by name, as a field of the target: carried though the target is right without it. */
  const named = (field: string): boolean => asked !== null && asked.has(`${to.key}.${field}`);
  const removed = new Set((options.removed ?? []).filter((one) => one.link === link.key).map((one) => one.to));
  const filled = new Set(link.fills.map((one) => one.to));

  const fills: StudioLinkFill[] = [];
  for (const target of to.fields) {
    if (!fillable(target) || target.key === to.signFieldKey || filled.has(target.key) || removed.has(target.key)) continue;
    const source = carrierOf(from, target);
    if (source === undefined) continue;
    if (!named(target.key) && !(targetNeeds(to, target.key) && (isAsked(to, target.key) || isAsked(from, source.key)))) continue;
    fills.push({ to: target.key, kind: 'field', field: source.key });
  }

  let carry: HeldLinkCarry | null = null;
  const fixed = link.fills.find((one) => one.to === to.signFieldKey);
  const choice = inOutChoiceOf(from, to);
  if (
    fixed !== undefined &&
    fixed.kind === 'const' &&
    choice !== null &&
    choice.byKey &&
    !conditionsRead(link, choice.field.key) &&
    !removed.has(fixed.to) &&
    (isAsked(to, fixed.to) || isAsked(from, choice.field.key))
  ) {
    carry = { to: fixed.to, field: choice.field.key, replaces: fixed.value };
  }
  return fills.length === 0 && carry === null ? null : { link: link.key, fills, carry };
};

/** For each link into or out of `recordKeys`, the fills it misses that its source holds; links that miss none are left out. */
export const heldLinkFills = (draft: StudioDraft, recordKeys: readonly string[], options: HeldLinkFillOptions = {}): HeldLinkFills[] => {
  const keys = new Set(recordKeys);
  return draft.links.filter((link) => keys.has(link.from) || keys.has(link.to)).flatMap((link) => missingOf(draft, link, options) ?? []);
};

/** A link's fills with what it misses: the fixed in/out option replaced by the carried choice in its place, the new fills after. */
export const heldLinkFillsOf = (link: StudioLink, held: HeldLinkFills): StudioLinkFill[] => [
  ...link.fills.map((fill): StudioLinkFill => (held.carry !== null && fill.to === held.carry.to ? { to: fill.to, kind: 'field', field: held.carry.field } : fill)),
  ...held.fills,
];

/**
 * The ops that keep the links into or out of `recordKeys` whole: one
 * `update_link` per link that gains a fill, holding its whole new fills
 * (`update_link` sets them all). Read against the draft as it stands when
 * they apply: a turn calls it after its fields are in.
 */
export const heldLinkFillOps = (draft: StudioDraft, recordKeys: readonly string[], options: HeldLinkFillOptions = {}): Array<Extract<StudioOp, { op: 'update_link' }>> =>
  heldLinkFills(draft, recordKeys, options).map((held) => {
    const link = draft.links.find((one) => one.key === held.link)!;
    return { op: 'update_link', link: link.key, fills: heldLinkFillsOf(link, held) };
  });
